/**
 * The jobs inbox — the screen the app opens on (§13.7).
 *
 * Sorted by what closes soonest, with a big Quote button. An RFQ that expires
 * unanswered is the most expensive thing that happens here: the vendor loses
 * the work *and* their response-time average, which decides whether they are
 * invited next time.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type ActiveJob, type Invitation, type JobsResponse } from '../lib/api';
import { useSession } from '../lib/session';
import { useI18n, useT, capabilityLabel } from '../lib/i18n';
import { formatINR, formatDue, formatClosesIn, isClosingSoon, daysUntil } from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Meta,
  PageHero,
  SectionTitle,
  Spinner,
  type Tone,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

export function JobsPage() {
  const { t } = useI18n();
  const T = useT();
  const { vendor } = useSession();
  const [data, setData] = useState<JobsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.get<JobsResponse>('/v1/partner/jobs'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;

  const invitations = data?.invitations ?? [];
  const active = data?.active ?? [];
  const open = invitations.filter((j) => !j.quoted).length;
  const inHand = active.reduce((sum, j) => sum + BigInt(j.awardedMicros ?? '0'), 0n);

  const hero = (
    <PageHero
      icon="jobs"
      eyebrow={vendor?.businessName}
      title={t('jobs.title')}
      sub={T(
        'नए काम पर जल्दी रेट भेजें — जल्दी जवाब देने वालों को अगला काम पहले मिलता है।',
        'Quote new work quickly — fast replies get the next job first.',
      )}
      stats={[
        { label: T('नए काम', 'New requests'), value: String(open) },
        { label: T('चल रहे', 'In progress'), value: String(active.length) },
        { label: T('काम की कीमत', 'Work in hand'), value: formatINR(inHand) },
      ]}
    />
  );

  // An unverified vendor receives no RFQs (§13.7). Saying so beats an empty
  // list they check every hour wondering whether the app is broken.
  if (vendor?.status === 'pending' && invitations.length === 0 && active.length === 0) {
    return (
      <>
        {hero}
        <EmptyState icon="shield" title={t('jobs.pendingTitle')} body={t('jobs.pendingBody')} />
      </>
    );
  }

  if (invitations.length === 0 && active.length === 0) {
    return (
      <>
        {hero}
        <EmptyState icon="jobs" title={t('jobs.emptyTitle')} body={t('jobs.emptyBody')} />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {hero}
      {invitations.length > 0 && (
        <section>
          <SectionTitle count={invitations.length}>{t('jobs.newRequests')}</SectionTitle>
          <ul className="space-y-3">
            {invitations.map((job) => (
              <li key={job.quoteId}>
                <InvitationCard job={job} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {active.length > 0 && (
        <section>
          <SectionTitle count={active.length}>{t('jobs.active')}</SectionTitle>
          <ul className="space-y-3">
            {active.map((job) => (
              <li key={job.jobId}>
                <ActiveCard job={job} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The job's kind as a coloured tile: what a vendor scans for first. */
function KindTile() {
  return (
    <span className="icon-tile h-11 w-11">
      <Icon name="printer" className="h-6 w-6" />
    </span>
  );
}

function InvitationCard({ job }: { job: Invitation }) {
  const { t, lang } = useI18n();
  const closesIn = formatClosesIn(job.closesAt, lang);
  const urgent = isClosingSoon(job.closesAt);
  const soon = daysUntil(job.neededBy) <= 2;

  return (
    <Card className="relative overflow-hidden">
      {!job.quoted && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-1.5"
          style={{ backgroundImage: 'linear-gradient(var(--sec-from), var(--sec-to))' }}
        />
      )}
      <div className="flex items-start gap-3">
        <KindTile />
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-bold leading-snug">{job.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            {closesIn && (
              <Badge tone={urgent ? 'bad' : 'info'} pulse={urgent}>
                {closesIn} {t('jobs.closesIn')}
              </Badge>
            )}
            <span className="text-sm text-muted">{capabilityLabel(job.capability, lang)}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
            <Meta icon="listings">{job.city}</Meta>
            <Meta icon="clock">
              <span className={soon ? 'font-semibold text-bad' : ''}>
                {formatDue(job.neededBy, lang)}
              </span>
            </Meta>
          </div>
        </div>
      </div>

      {job.quoted ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-panel-2 px-4 py-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-good">
            <Icon name="check" className="h-4 w-4" strokeWidth={2.6} />
            {t('jobs.quoted')}
          </span>
          <span className="tnum font-display text-lg font-bold">
            {formatINR(job.quotedAmountMicros ?? '0')}
          </span>
        </div>
      ) : (
        <Link to={`/jobs/${job.jobId}/quote`} className="btn-primary mt-4">
          {t('jobs.quote')}
          <Icon name="arrow" className="h-5 w-5" strokeWidth={2.2} />
        </Link>
      )}
    </Card>
  );
}

function ActiveCard({ job }: { job: ActiveJob }) {
  const { t, lang } = useI18n();
  const T = useT();

  const STATUS: Record<string, [string, Tone]> = {
    awarded: [t('jobs.awarded'), 'info'],
    in_progress: [T('काम चल रहा है', 'In progress'), 'warn'],
    delivered: [t('jobs.awaitingVerify'), 'neutral'],
    verified: [T('पास ✓', 'Checked ✓'), 'good'],
    disputed: [T('शिकायत', 'Disputed'), 'bad'],
    cancelled: [T('रद्द', 'Cancelled'), 'neutral'],
  };
  const [label, tone] = STATUS[job.status] ?? [job.status, 'neutral'];
  const late = daysUntil(job.neededBy) < 0 && !['delivered', 'verified'].includes(job.status);

  return (
    <Card>
      <div className="flex items-start gap-3">
        <KindTile />
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-bold leading-snug">{job.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={tone} pulse={job.status === 'awarded'}>
              {label}
            </Badge>
            <span className="text-sm text-muted">{capabilityLabel(job.capability, lang)}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <Meta icon="listings">{job.city}</Meta>
              <Meta icon="clock">
                <span className={late ? 'font-semibold text-bad' : ''}>
                  {formatDue(job.neededBy, lang)}
                </span>
              </Meta>
            </div>
            {job.awardedMicros && (
              <span className="tnum font-display text-lg font-bold">
                {formatINR(job.awardedMicros)}
              </span>
            )}
          </div>
        </div>
      </div>

      {job.status !== 'delivered' &&
        job.status !== 'verified' &&
        job.status !== 'cancelled' && (
          <Link to={`/jobs/${job.jobId}/proof`} className="btn-primary mt-4">
            <Icon name="camera" className="h-5 w-5" />
            {t('jobs.uploadProof')}
          </Link>
        )}

      {job.status === 'delivered' && (
        <p className="mt-4 flex items-start gap-2 rounded-2xl bg-panel-2 p-3 text-sm text-muted">
          <Icon name="shield" className="mt-0.5 h-4 w-4 shrink-0" />
          {t('proof.paidAfter')}
        </p>
      )}
    </Card>
  );
}
